import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { useSelector } from 'react-redux';
import { Controller } from 'react-hook-form';
import { FormInputAddress } from './FormInputAddress';
import FormReactSelectResidenceInput from './FormReactSelectResidenceInput';
import { FormInputText } from './FormInputText';

// v2 of formInputs/FormInputAddressAutoComplete — same prop contract and
// Google Places autocomplete logic, restyled via the v2 FormInputAddress/
// FormReactSelectResidenceInput/FormInputText it composes. Forked (not
// edited in place) because the v1 original is shared with Cauzioni's
// StepGaranzia.jsx via Commercial/components/parts/InputByType.jsx.
function FormInputAddressAutoComplete({
  name,
  isValid,
  errors,
  setValue,
  control,
  formData,
  isModifyState,
}) {
  const [showManualAddress, setShowManualAddress] = useState(false);
  const [prefillDone, setPrefillDone] = useState(false);
  const [userSelectedAddress, setUserSelectedAddress] = useState(false);
  const [googleMaps, setGoogleMaps] = useState(null);
  const communesRef = useRef();
  let { municipalities } = useSelector((store) => store.forms.commercial);
  const { municipalities: backofficeMunicipalities } = useSelector((store) => store.formsBackoffice.commercial);

  if ((!municipalities || municipalities.length === 0) && backofficeMunicipalities && backofficeMunicipalities.length > 0) {
    municipalities = backofficeMunicipalities;
  }

  useEffect(() => {
    if (errors?.[name]?.postal_code) {
      setShowManualAddress(true);
    }
  }, [errors?.[name]?.postal_code]);

  useEffect(() => {
    if (
      municipalities
      && municipalities.length > 0
      && communesRef.current === undefined
    ) communesRef.current = municipalities;
  }, [municipalities]);

  const handleClearInputs = () => {
    setValue(`${name}.address`, '', {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${name}.postal_code`, '', {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${name}.municipality_name`, '', {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${name}.ministerial_municipality_code`, '', {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${name}.province_code`, '', {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${name}.country_code_uic`, '', {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${name}.address_type`, '', {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${name}.main_address_flag`, '', {
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (isModifyState) {
      return () => {};
    }

    const loader = new Loader({
      apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
      version: 'weekly',
      libraries: ['places'],
    });

    loader.load().then((google) => {
      setGoogleMaps(google);
      const autocomplete = new google.maps.places.Autocomplete(
        document.getElementById('autoCompleteAddress'),
        {
          // Request place_id so we can fallback to geocoding if street number is missing
          fields: ['formatted_address', 'name', 'address_components', 'place_id'],
          types: ['address'],
          componentRestrictions: { country: 'it' },
        },
      );
      const geocoder = new google.maps.Geocoder();

      const actionTakeOnAutocomplete = async () => {
        const place = autocomplete.getPlace();

        if (place === undefined || place.address_components === undefined) {
          // Only clear if the user has actually emptied the input string manually
          // place.name contains the current text in the input even if no place was selected
          if (!place?.name) {
            handleClearInputs();
          }
          return;
        }

        // Mark that user has selected an address from autocomplete
        setUserSelectedAddress(true);

        // Prefer robust checks with types.includes
        const address = place.address_components.find((c) => c.types.includes('route'))?.long_name || '';
        const houseNumberFromPlace = place.address_components.find((c) => c.types.includes('street_number'))?.long_name;
        const municipalityName = place.address_components.find((c) => c.types.includes('administrative_area_level_3'))?.long_name;

        const commune = communesRef.current.find((m) => m.name === municipalityName);

        if (commune === undefined) {
          handleClearInputs();
          return;
        }

        const residence_commune_code = commune.cadastral_code;
        const residence_province_code = commune.communal_territory.car_plate_symbol;
        const postal_code = place.address_components.find((c) => c.types.includes('postal_code'))?.long_name ?? commune.postal_code;

        // Check if postal_code contains any letters
        if (!postal_code || /[a-zA-Z]/.test(postal_code)) {
          setShowManualAddress(true);
        }

        // If street_number wasn't provided by the place, try geocoding the place_id as a fallback
        let houseNumber = houseNumberFromPlace;
        if (!houseNumber && place.place_id) {
          try {
            /* eslint-disable no-await-in-loop */
            const geocodePromise = () => new Promise((resolve) => {
              geocoder.geocode({ placeId: place.place_id }, (results, status) => resolve({ results, status }));
            });
            // eslint-disable-next-line no-await-in-loop
            const { results, status } = await geocodePromise();
            if (status === 'OK' && results?.[0]) {
              houseNumber = results[0].address_components.find((c) => c.types.includes('street_number'))?.long_name;
            }
          } catch (e) {
            // ignore geocode fallback errors and continue without house number
            // console.debug(e);
          }
        }

        setValue(`${name}.address`, address, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue(`${name}.house_number`, houseNumber || '', {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue(`${name}.postal_code`, postal_code, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue(`${name}.municipality_name`, commune.name, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue(`${name}.ministerial_municipality_code`, residence_commune_code, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue(`${name}.province_code`, residence_province_code, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue(`${name}.country_code_uic`, 'IT', {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue(`${name}.address_type`, 6, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue(`${name}.main_address_flag`, 'S', {
          shouldTouch: true,
          shouldValidate: true,
        });
      };

      autocomplete.addListener('place_changed', actionTakeOnAutocomplete);
    });

    return () => {
      if (loader) {
        loader.reset();
        delete window.google;
        Loader.instance = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (!googleMaps) return; // wait until loader has finished
    if (!formData?.[name].address || isValid(`${name}.address`) || prefillDone || userSelectedAddress) return;

    const service = new googleMaps.maps.places.AutocompleteService();
    const geocoder = new googleMaps.maps.Geocoder();

    service.getPlacePredictions(
      {
        input: formData?.[name].address,
        componentRestrictions: { country: 'it' },
      },
      (predictions, status) => {
        if (status === googleMaps.maps.places.PlacesServiceStatus.OK && predictions?.length) {
          const placeId = predictions[0].place_id;

          geocoder.geocode({ placeId }, (results, geoStatus) => {
            if (geoStatus === 'OK' && results?.[0]) {
              const place = results[0];
              const components = place.address_components;

              const address = components.find((c) => c.types.includes('route'))?.long_name;
              const houseNumber = components.find((c) => c.types.includes('street_number'))?.long_name;
              const postal_code = components.find((c) => c.types.includes('postal_code'))?.long_name;
              const municipality_name = components.find((c) => c.types.includes('administrative_area_level_3'))?.long_name;

              const commune = communesRef.current?.find((m) => m.name === municipality_name);
              if (!commune) return;

              setValue(`${name}.address`, address || '', { shouldTouch: true, shouldValidate: true });
              // Only set house_number if it's not already filled
              if (!formData?.[name]?.house_number) {
                setValue(`${name}.house_number`, houseNumber || '', { shouldTouch: true, shouldValidate: true });
              }
              // Only set postal_code if it's not already filled
              if (!formData?.[name]?.postal_code) {
                setValue(`${name}.postal_code`, postal_code || commune.postal_code, { shouldTouch: true, shouldValidate: true });
              }
              setValue(`${name}.municipality_name`, municipality_name || '', { shouldTouch: true, shouldValidate: true });
              setValue(`${name}.ministerial_municipality_code`, commune.cadastral_code, { shouldTouch: true, shouldValidate: true });
              setValue(`${name}.province_code`, commune.communal_territory.car_plate_symbol, { shouldTouch: true, shouldValidate: true });
              setValue(`${name}.country_code_uic`, 'IT', { shouldTouch: true, shouldValidate: true });
              setValue(`${name}.address_type`, 6, { shouldTouch: true, shouldValidate: true });
              setValue(`${name}.main_address_flag`, 'S', { shouldTouch: true, shouldValidate: true });

              setPrefillDone(true);
            }
          });
        }
      },
    );
  }, [googleMaps, formData?.[name].address, municipalities]);

  return (
    <>
      <Controller
        name={`${name}.address`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Controller
            name={`${name}.house_number`}
            control={control}
            render={({ field: { value: houseValue, onChange: onHouseChange } }) => (
              <FormInputAddress
                valueAddress={value || ''}
                valueHouse={houseValue || ''}
                label="Cerca l'indirizzo (comune se necessario)"
                subText="Se ci sono più strade con lo stesso nome, scrivi nell'indirizzo il comune"
                onAddressChange={(newValue) => {
                  onChange(newValue);
                }}
                onHouseNumberChange={(newValue) => {
                  onHouseChange(newValue);
                }}
                addressError={errors[name]?.address?.message}
                houseNumberError={errors[name]?.house_number?.message}
                validAddress={isValid(`${name}.address`)}
                validHouseNumber={isValid(`${name}.house_number`)}
                addressId="autoCompleteAddress"
                placeholder="Inserisci Indirizzo..."
                housePlaceholder="Es. 10"
              />
            )}
          />
        )}
      />

      <Controller
        name={`${name}.ministerial_municipality_code`}
        control={control}
        render={({ field: { value: municipalityCode } }) => (
          <Controller
            name={`${name}.province_code`}
            control={control}
            render={({ field: { value: provinceCode } }) => (
              <Controller
                name={`${name}.postal_code`}
                control={control}
                render={({ field: { value: postalCode } }) => (
                  <>
                    {municipalityCode !== ''
                      && provinceCode !== ''
                      && postalCode !== '' && (
                        <div>
                          <p
                            className="m-0 fw-bold"
                            style={{ fontSize: '15px', marginTop: '-10px' }}
                          >
                            Comune:
                            {' '}
                            {
                              municipalities
                                .filter(
                                  (opt) => opt.cadastral_code === municipalityCode,
                                )
                                .map((o) => ({
                                  value: o.cadastral_code,
                                  label: `${o.name} (${o.communal_territory.car_plate_symbol})`,
                                }))[0]?.label
                            }
                          </p>
                        </div>
                    )}
                  </>
                )}
              />
            )}
          />
        )}
      />

      <div className="text-dark">
        <p
          className="mt-1 mb-3 fw-bold"
          style={{ fontSize: '13px', marginTop: '-10px' }}
        >
          oppure
          {' '}
          <button
            type="button"
            className="fw-bold text-decoration-underline border-0 text-success bg-transparent"
            onClick={() => setShowManualAddress(!showManualAddress)}
          >
            {showManualAddress ? 'nascondi' : 'inserisci manualmente'}
          </button>
        </p>
      </div>
      {showManualAddress && (
        <Controller
          name={`${name}.ministerial_municipality_code`}
          control={control}
          render={({ field: { value: municipalityCode } }) => (
            <Controller
              name={`${name}.province_code`}
              control={control}
              render={({ field: { value: provinceCode } }) => (
                <Controller
                  name={`${name}.postal_code`}
                  control={control}
                  render={({ field: { value: postalCode, onChange: onPostalChange } }) => (
                    <FormReactSelectResidenceInput
                      label="Residenza (Dall'indirizzo)"
                      options={municipalities}
                      values={{
                        residence_commune_code: municipalityCode || '',
                        residence_province_code: provinceCode || '',
                        postal_code: postalCode || '',
                      }}
                      onChange={(val) => {
                        const commune = municipalities.find(
                          (m) => m.cadastral_code === val.residence_commune_code,
                        );

                        setValue(`${name}.ministerial_municipality_code`, val.residence_commune_code, {
                          shouldTouch: true,
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue(`${name}.province_code`, val.residence_province_code, {
                          shouldTouch: true,
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue(`${name}.postal_code`, val.postal_code, {
                          shouldTouch: true,
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue(`${name}.municipality_name`, commune?.name || '', {
                          shouldTouch: true,
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue(`${name}.country_code_uic`, 'IT', {
                          shouldTouch: true,
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue(`${name}.address_type`, 6, {
                          shouldTouch: true,
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue(`${name}.main_address_flag`, 'S', {
                          shouldTouch: true,
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      disabled={!(
                        errors[name]?.ministerial_municipality_code?.message
                        || errors[name]?.province_code?.message
                      )}
                      error={errors[name]?.ministerial_municipality_code?.message}
                      valid={isValid(`${name}.ministerial_municipality_code`)}
                    >
                      <FormInputText
                        label="Codice Postale"
                        placeholder="-Codice Postale-"
                        value={postalCode || ''}
                        onChange={onPostalChange}
                        error={errors[name]?.postal_code?.message}
                        valid={isValid(`${name}.postal_code`)}
                      />
                    </FormReactSelectResidenceInput>
                  )}
                />
              )}
            />
          )}
        />
      )}
    </>
  );
}

export default FormInputAddressAutoComplete;
